import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { LCA_CATEGORIES, LCA_STAGES } from "../../lca/lcaTypes";

let RechartsAvailable = true;
let PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid;
try {
  const recharts = require("recharts");
  PieChart = recharts.PieChart;
  Pie = recharts.Pie;
  Cell = recharts.Cell;
  Tooltip = recharts.Tooltip;
  ResponsiveContainer = recharts.ResponsiveContainer;
  BarChart = recharts.BarChart;
  Bar = recharts.Bar;
  XAxis = recharts.XAxis;
  YAxis = recharts.YAxis;
  CartesianGrid = recharts.CartesianGrid;
} catch {
  RechartsAvailable = false;
}

const PIE_COLORS = ["#2f6046", "#245040", "#4f6b5a", "#d8dcd1", "#dfe5d8", "#d5dacc"];
const PIE_TOOLTIP_COLORS = ["#2f6046", "#245040", "#4f6b5a", "#d8dcd1", "#dfe5d8", "#d5dacc"];

function formatKg(n) {
  const v = Number(n || 0);
  const rounded = Math.round(v);
  return `${rounded.toLocaleString()} kgCO2e`;
}

export default function ResultsPanel({
  computedProject,
  buildingArea,
  buildingLifeYears
}) {
  const denom = (() => {
    const area = Number(buildingArea);
    const life = Number(buildingLifeYears);
    if (Number.isFinite(area) && area > 0 && Number.isFinite(life) && life > 0) return area * life;
    return null;
  })();

  const toPerM2Yr = (v) => {
    if (!denom) return null;
    return Number(v || 0) / denom;
  };

  const categoryData = useMemo(() => {
    return LCA_CATEGORIES.map((c) => {
      const v = computedProject?.byCategory?.[c.key]?.total || 0;
      return { key: c.key, name: c.label, value: v, per: toPerM2Yr(v) };
    })
      .filter((d) => d.key !== "energy")
      .filter((d) => d.value > 0);
  }, [computedProject, denom]);

  const stageRows = useMemo(() => {
    return LCA_STAGES.map((s) => ({
      key: s.key,
      label: s.label,
      value: computedProject?.stageTotals?.[s.key] || 0,
      per: toPerM2Yr(computedProject?.stageTotals?.[s.key] || 0)
    })).filter((r) => Math.abs(r.value) > 0.000001);
  }, [computedProject, denom]);

  const moduleChartData = useMemo(() => {
    const totals = computedProject?.stageTotals || {};
    const groups = [
      { key: "A1-A3", label: "A1-A3", value: totals.A1A3 || 0 },
      { key: "A4-A5", label: "A4-A5", value: (totals.A4 || 0) + (totals.A5 || 0) },
      { key: "B1-B7", label: "B1-B7", value: (totals.B4 || 0) + (totals.B6 || 0) },
      { key: "C1-C4", label: "C1-C4", value: totals.C1C4 || 0 },
      { key: "D", label: "D", value: totals.D || 0 }
    ];
    return groups.map((g, idx) => ({
      ...g,
      per: toPerM2Yr(g.value),
      fill: PIE_COLORS[idx % PIE_COLORS.length]
    }));
  }, [computedProject, denom]);

  const embodiedVsOperational = useMemo(() => {
    const totals = computedProject?.stageTotals || {};
    const operational = totals.B6 || 0;
    const total = computedProject?.total || 0;
    const embodied = total - operational;
    return [
      {
        key: "embodied",
        label: "Embodied carbon",
        value: embodied,
        per: toPerM2Yr(embodied),
        fill: "#2f6046"
      },
      {
        key: "operational",
        label: "Operational carbon (B6)",
        value: operational,
        per: toPerM2Yr(operational),
        fill: "#f59e0b"
      }
    ].filter((d) => Math.abs(d.value) > 0.000001);
  }, [computedProject, denom]);

  const totalPerM2Yr = toPerM2Yr(computedProject?.total || 0);

  const renderModuleTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0];
    const value = Number(entry?.value || 0);
    const unit = denom ? "kgCO2e/m²/yr" : "kgCO2e";
    const display = denom ? value.toFixed(3) : Math.round(value).toLocaleString();
    return (
      <div className="rounded-lg border border-[#d5dacc] bg-white/95 px-3 py-2 shadow-sm">
        <div className="text-xs text-[#4f6b5a]">{entry?.payload?.label || entry?.name}</div>
        <div className="text-sm font-semibold text-[#2b4331]">
          {display} {unit}
        </div>
      </div>
    );
  };

  const renderPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0] || {};
    const value = Number(entry?.value || 0);
    const name = entry?.name || entry?.payload?.name || entry?.payload?.label || "Impact";
    const color = entry?.payload?.fill || PIE_TOOLTIP_COLORS[0];
    const unit = denom ? "kgCO2e/m²/yr" : "kgCO2e";
    const display = denom ? value.toFixed(3) : Math.round(value).toLocaleString();
    return (
      <div className="rounded-xl border border-[#d5dacc] bg-white/95 px-3 py-2 shadow-lg">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          <div className="text-xs font-semibold text-[#2b4331]">{name}</div>
        </div>
        <div className="mt-1 text-[11px] text-[#4f6b5a] uppercase tracking-wide">Impact</div>
        <div className="text-sm font-semibold text-[#2b4331]">
          {display} {unit}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <Card className="border border-[#d5dacc] bg-white shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[#2b4331]">Results</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm text-[#4f6b5a]">
              Total building GWP:
              <span className="ml-2 font-semibold text-[#2b4331]">
                {denom ? `${(totalPerM2Yr || 0).toFixed(3)} kgCO2e/m²/yr` : formatKg(computedProject?.total || 0)}
              </span>
              {!denom && (
                <span className="ml-2 text-xs text-[#4f6b5a]">
                  Set building area and life to see kgCO2e/m²/yr.
                </span>
              )}
            </div>
            <Badge variant="secondary" className="text-[10px] border border-[#d5dacc] bg-[#eef2e7] text-[#2b4331]">
              A1 to D included
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#d5dacc] bg-white p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-[#2b4331] mb-1">Category breakdown</div>
              </div>

              {categoryData.length === 0 ? (
                <div className="text-sm text-[#4f6b5a]">Add components with EPDs to see results.</div>
              ) : (
                <>
                  {RechartsAvailable ? (
                    <div className="h-[280px] w-full">
                      <ResponsiveContainer>
                        <PieChart tabIndex={-1} style={{ outline: "none" }}>
                          <Pie
                            data={categoryData}
                            dataKey={(d) => (denom ? d.per || 0 : d.value)}
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={110}
                            paddingAngle={2}
                          >
                            {categoryData.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#ffffff" strokeWidth={0.5} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={renderPieTooltip}
                            isAnimationActive={false}
                            wrapperStyle={{ transition: "none" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-sm text-[#4f6b5a]">Recharts not detected. Install recharts to enable the pie chart.</div>
                  )}

                  <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {categoryData.map((d) => (
                      <div
                        key={d.key}
                        className="w-full flex items-center justify-between rounded-lg border px-3 py-2 text-left border-[#d5dacc] bg-white"
                      >
                        <div className="text-sm font-medium text-[#2b4331]">{d.name}</div>
                        <div className="text-xs font-semibold text-[#2b4331]">
                          {denom ? `${(d.per || 0).toFixed(3)} kgCO2e/m²/yr` : formatKg(d.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-xl border border-[#d5dacc] bg-white p-3 space-y-3">
              <div className="text-sm font-semibold text-[#2b4331] mb-1">Embodied vs operational</div>
              {embodiedVsOperational.length === 0 ? (
                <div className="text-sm text-[#4f6b5a]">No operational energy impact yet.</div>
              ) : RechartsAvailable ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer>
                    <PieChart tabIndex={-1} style={{ outline: "none" }}>
                      <Pie
                        data={embodiedVsOperational}
                        dataKey={(d) => (denom ? d.per || 0 : d.value)}
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={2}
                      >
                        {embodiedVsOperational.map((entry) => (
                          <Cell key={entry.key} fill={entry.fill} stroke="#ffffff" strokeWidth={0.5} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={renderPieTooltip}
                        isAnimationActive={false}
                        wrapperStyle={{ transition: "none" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-[#4f6b5a]">Recharts not detected. Install recharts to see the pie chart.</div>
              )}

              {embodiedVsOperational.length > 0 && (
                <div className="pt-2 space-y-2">
                  {embodiedVsOperational.map((d) => (
                    <div key={d.key} className="flex items-center justify-between rounded-lg border border-[#d5dacc] px-3 py-2">
                      <div className="text-sm font-medium text-[#2b4331]">{d.label}</div>
                      <div className="text-xs font-semibold text-[#2b4331]">
                        {denom ? `${(d.per || 0).toFixed(3)} kgCO2e/m²/yr` : formatKg(d.value)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#d5dacc] bg-white p-3 space-y-3">
              <div className="text-sm font-semibold text-[#2b4331] mb-1">Module impacts (A1-D)</div>
              {moduleChartData.length === 0 ? (
                <div className="text-sm text-[#4f6b5a]">No module impacts yet.</div>
              ) : RechartsAvailable ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer>
                    <BarChart
                      data={moduleChartData}
                      barGap={10}
                      barCategoryGap={14}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke="#eef2e7" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={{ stroke: "#d5dacc" }}
                        tick={{ fill: "#4f6b5a", fontSize: 12 }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={{ stroke: "#d5dacc" }}
                        tick={{ fill: "#4f6b5a", fontSize: 12 }}
                      />
                      <Tooltip
                        content={renderModuleTooltip}
                        isAnimationActive={false}
                        wrapperStyle={{ transition: "none" }}
                      />
                      <Bar
                        dataKey={denom ? "per" : "value"}
                        radius={[8, 8, 0, 0]}
                        isAnimationActive={false}
                        maxBarSize={80}
                      >
                        {moduleChartData.map((entry) => (
                          <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-[#4f6b5a]">Recharts not detected. Install recharts to see the module chart.</div>
              )}
            </div>

            <div className="rounded-xl border border-[#d5dacc] bg-white p-3">
              <div className="text-sm font-semibold text-[#2b4331] mb-2">Stage totals</div>

              {stageRows.length === 0 ? (
                <div className="text-sm text-[#4f6b5a]">No computed stage totals yet.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {stageRows.map((r) => (
                    <div
                      key={r.key}
                      className="flex items-center justify-between rounded-lg border border-[#d5dacc] px-3 py-2"
                    >
                      <div className="text-sm text-[#2b4331]">{r.label}</div>
                      <div className="text-xs font-semibold text-[#2b4331]">
                        {denom ? `${(r.per || 0).toFixed(3)} kgCO2e/m²/yr` : formatKg(r.value)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
